import { Checkbox, Select } from 'antd';
import commaNumber from 'comma-number';
import { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import { CenterFlex, Flex, Space, VFlex } from '@/components/base';
import SearchInput from '@/components/Inputs/SearchInput';
import List from '@/components/List/List';
import StyledPagination from '@/components/Pagination/Pagination';
import type { TriggerPos } from '@/components/Select/types';
import Typo from '@/components/Typography/Typo';
import AMP_KEYS from '@/configs/amp-track-keys';
import STORAGE_KEYS from '@/configs/local-storage-keys';
import CompanySelectModal from '@/features/Account/components/CompanySelectModal';
import CustomerSelectModal from '@/features/Contact/CustomerSelectModal';
import useSingleData from '@/hooks/pure/useSingleData';
import useWorkspaceStorage from '@/hooks/pure/useWorkspaceStorage';
import service from '@/services/index';
import { PageHeader } from '@/shared/ui';
import useDropdownStore from '@/stores/pure/DropdownStore';
import {
  CUSTOMER_QUERY_DATE_RANGE,
  CUSTOMER_QUERY_DIRECTION,
  CUSTOMER_QUERY_DOMAIN,
  CUSTOMER_QUERY_SENTIMENT,
  CUSTOMER_QUERY_SORT_BY,
} from '@/types/customerConst';
import { getTimezoneDayjs } from '@/utils/time';
import { track } from '@/utils/track/track';

import ChartLabels, { SENTIMENT_CHART_OFFSET } from './components/ChartLabels';
import CustomerFilterArea from './components/CustomerFilterArea';
import CustomerRow from './components/CustomerRow';
import CustomerTableStyles from './components/CustomerTableStyles';
import SentimentSelectTabItem from './components/SentimentSelectTabItem';
import useFetchAccountSentimentList from './hooks/useFetchAccountSentimentList';
import useFetchContactSentimentList from './hooks/useFetchContactSentimentList';

const { StyledCol } = CustomerTableStyles;

export type CustomerQueryState = {
  domain: CUSTOMER_QUERY_DOMAIN;
  agent?: string;
  segment?: string;
  segment_value?: {
    value: string;
    label: string;
  };
  sortby: CUSTOMER_QUERY_SORT_BY;
  sentiment?: CUSTOMER_QUERY_SENTIMENT;
  search?: string;
};

type ListState = {
  loading: boolean;
  list: CustomerSentimentContactItem[] | null;
  page: number;
};

type PageParams = {
  targetPage?: number;
  listState?: ListState;
};

const LENGTH_PER_PAGE = 50;

const CustomerSentimentPage = () => {
  const setCurrDropdown = useDropdownStore((state) => state.setCurrDropdown);
  const [dateRange, setDateRange] = useWorkspaceStorage(
    STORAGE_KEYS.CUSTOMER_VIEW_FILTER_STATE_DATE_RANGE,
    CUSTOMER_QUERY_DATE_RANGE.DAY7,
  );
  const { fetchContactSentimentList } = useFetchContactSentimentList();
  const { fetchAccountSentimentList } = useFetchAccountSentimentList();

  const [state, setState] = useWorkspaceStorage<CustomerQueryState>(
    STORAGE_KEYS.CUSTOMER_VIEW_FILTER_STATE,
    {
      domain: CUSTOMER_QUERY_DOMAIN.ACCOUNT,
      sortby: CUSTOMER_QUERY_SORT_BY.NEWEST,
      agent: undefined,
      segment: undefined,
      segment_value: undefined,
      sentiment: undefined,
      search: undefined,
    },
  );

  const fetchDist = useCallback(async () => {
    const params = {
      cp_date_range: dateRange,
      cp_segment: state.segment,
      cp_segment_value: state.segment_value?.value,
      cp_agent: state.agent,
      cp_name: state.search,
    };
    if (state.domain === CUSTOMER_QUERY_DOMAIN.ACCOUNT) {
      return await service.customer.fetchAccountSentimentDist(params);
    }
    if (state.domain === CUSTOMER_QUERY_DOMAIN.CONTACT) {
      return await service.customer.fetchContactSentimentDist(params);
    }
    return null;
  }, [
    dateRange,
    state.agent,
    state.domain,
    state.segment,
    state.segment_value,
    state.search,
  ]);

  const { data: dist } = useSingleData(fetchDist);
  const [listState, setListState] = useState<ListState>({
    loading: false,
    list: null,
    page: 0,
  });

  const fetchList = useCallback(
    async (pageParam?: PageParams) => {
      let cursors: FetchCustomerCursorParams = {
        cp_direction: CUSTOMER_QUERY_DIRECTION.NEXT,
      };

      if (pageParam?.listState?.list) {
        if (pageParam?.targetPage && pageParam.targetPage !== 0) {
          if ((pageParam.listState?.page || 0) > pageParam.targetPage) {
            // to pageParam
            const cursorEl = pageParam.listState?.list?.[0];
            if (cursorEl) {
              cursors = {
                cp_ratio_cursor: cursorEl.negative_sentiment_ratio,
                cp_date_cursor: cursorEl.date,
                cp_created_cursor: cursorEl.created_at,
                cp_direction: CUSTOMER_QUERY_DIRECTION.PREV,
              };
            }
          }
          if ((pageParam.listState?.page || 0) < pageParam.targetPage) {
            // to nextPage
            const cursorEl =
              pageParam.listState?.list[pageParam.listState.list.length - 1];
            if (cursorEl) {
              cursors = {
                cp_ratio_cursor: cursorEl.negative_sentiment_ratio,
                cp_date_cursor: cursorEl.date,
                cp_created_cursor: cursorEl.created_at,
                cp_direction: CUSTOMER_QUERY_DIRECTION.NEXT,
              };
            }
          }
        }
      }

      const params = {
        cp_date_range: dateRange,
        cp_segment: state.segment,
        cp_segment_value: state.segment_value?.value,
        cp_agent: state.agent,
        cp_sort_by: state.sortby,
        cp_sentiment: state.sentiment,
        cp_name: state.search,
        // cp_search: state.search,
        ...cursors,
      };

      if (params.cp_segment) {
        if (!params.cp_segment_value) return;
      }

      if (state.domain === CUSTOMER_QUERY_DOMAIN.ACCOUNT) {
        setListState((prev) => ({
          ...prev,
          loading: true,
        }));
        const res = await fetchAccountSentimentList(params);
        setListState((prev) => ({
          ...prev,
          list: res,
          page: pageParam?.targetPage || 0,
          loading: false,
        }));
        return;
      }
      if (state.domain === CUSTOMER_QUERY_DOMAIN.CONTACT) {
        setListState((prev) => ({
          ...prev,
          loading: true,
        }));
        const res = await fetchContactSentimentList(params);
        setListState((prev) => ({
          ...prev,
          list: res,
          page: pageParam?.targetPage || 0,
          loading: false,
        }));
        return;
      }
    },
    [
      dateRange,
      state.agent,
      state.domain,
      state.segment,
      state.segment_value?.value,
      state.sentiment,
      state.sortby,
      state.search,
      fetchAccountSentimentList,
      fetchContactSentimentList,
    ],
  );

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const currCount = useMemo(() => {
    if (state.sentiment === CUSTOMER_QUERY_SENTIMENT.HAPPY)
      return dist?.happy || 0;
    if (state.sentiment === CUSTOMER_QUERY_SENTIMENT.UNHAPPY)
      return dist?.unhappy || 0;
    return dist?.count || 0;
  }, [dist?.count, dist?.happy, dist?.unhappy, state.sentiment]);

  const sentiments = [
    undefined,
    CUSTOMER_QUERY_SENTIMENT.HAPPY,
    CUSTOMER_QUERY_SENTIMENT.UNHAPPY,
  ];

  const [startDate, endDate] = useMemo(() => {
    const today = getTimezoneDayjs();

    switch (dateRange) {
      case CUSTOMER_QUERY_DATE_RANGE.DAY1:
        return [today.subtractVal(1, 'day').startOf('day'), today];
      case CUSTOMER_QUERY_DATE_RANGE.DAY7:
        return [today.subtractVal(7, 'day').startOf('day'), today];
      case CUSTOMER_QUERY_DATE_RANGE.DAY30:
        return [today.subtractVal(30, 'day').startOf('day'), today];
      case CUSTOMER_QUERY_DATE_RANGE.DAY90:
        return [today.subtractVal(90, 'day').startOf('day'), today];
      default:
        return [today.subtractVal(1, 'day').startOf('day'), today];
    }
  }, [dateRange]);

  const [hideNeutral, setHideNeutral] = useWorkspaceStorage(
    STORAGE_KEYS.CUSTOMER_VIEW_HIDE_NEUTRAL,
    false,
  );

  const [triggerElPos, setTriggerElPos] = useState<TriggerPos>({
    top: 0,
    left: 0,
  });

  return (
    <Style>
      <PageHeader
        title="Customers"
        desc="Easily find out customers at risk and take action proactively."
        topRightNode={
          <CustomerFilterArea
            state={state}
            setState={setState}
            dateRange={dateRange}
            setDateRange={setDateRange}
          />
        }
      />
      <StyledWrapper>
        <VFlex g={12}>
          {sentiments.map((sentiment, index) => (
            <SentimentSelectTabItem
              key={index}
              dist={dist}
              selected={state.sentiment === sentiment}
              sentiment={sentiment}
              onClick={(sentiment) => {
                track(AMP_KEYS.CUSTOMER_SENTIMENT_SELECT);
                setState({
                  ...state,
                  sentiment,
                });
              }}
            />
          ))}
        </VFlex>

        <StyledCustomerSentimentList>
          <StyledCustomerSentimentListHeader>
            <Flex g={20} a="center">
              <Typo type="label0" weight="sb">
                Customers ({commaNumber(currCount)})
              </Typo>
              <SearchInput
                placeholder="Search"
                value={state.search || ''}
                onFocus={(e) => {
                  setCurrDropdown(
                    'customer-sentiment-customer-select-dropdown',
                  );
                  setTriggerElPos({
                    top: e.currentTarget.getBoundingClientRect().top,
                    left: e.currentTarget.getBoundingClientRect().left,
                  });
                }}
                onChange={() => {}}
                onClickClear={() => {
                  setState({
                    ...state,
                    search: undefined,
                  });
                }}
              />
            </Flex>
            {state.domain === CUSTOMER_QUERY_DOMAIN.CONTACT && (
              <CustomerSelectModal
                name={'customer-sentiment-customer-select-dropdown'}
                toggleItem={(customer) => {
                  setCurrDropdown(null);
                  setState({
                    ...state,
                    search: customer?.name,
                  });
                }}
                company_info={null}
                clearable={false}
                triggerPos={triggerElPos}
              />
            )}
            {state.domain === CUSTOMER_QUERY_DOMAIN.ACCOUNT && (
              <CompanySelectModal
                name={'customer-sentiment-customer-select-dropdown'}
                toggleItem={(company) => {
                  setCurrDropdown(null);
                  setState({
                    ...state,
                    search: company?.name,
                  });
                }}
                clearable={false}
                triggerPos={triggerElPos}
              />
            )}

            <Flex a="center">
              <Typo type="label2" className="text-gray-600" weight="r">
                Sort by
              </Typo>
              <Select
                value={state.sortby}
                variant="borderless"
                onSelect={(v) =>
                  setState({
                    ...state,
                    sortby: Number(v),
                  })
                }
                dropdownStyle={{
                  width: '160px',
                }}
              >
                <Select.Option
                  className="sort-option"
                  value={CUSTOMER_QUERY_SORT_BY.NEWEST}
                >
                  Newest
                </Select.Option>

                <Select.Option
                  className="sort-option"
                  value={CUSTOMER_QUERY_SORT_BY.HIGHEST_NEGATIVE_COUNT}
                >
                  Negative volume
                </Select.Option>

                <Select.Option
                  className="sort-option"
                  value={CUSTOMER_QUERY_SORT_BY.HIGHEST_NEGATIVE_SHARES}
                >
                  Negative share
                </Select.Option>
              </Select>
            </Flex>
          </StyledCustomerSentimentListHeader>

          <Space h={18} />

          <StyledCustomerSentimentTableWrapper>
            <StyledTableHeader>
              <StyledCol className="col-name">
                <Space w={18} />
                <Typo type="label2" className="text-gray-700" weight="r">
                  Name
                </Typo>
              </StyledCol>

              <StyledCol className="col-negative">
                <Typo type="label2" className="text-gray-700" weight="r">
                  Negative
                </Typo>
              </StyledCol>

              <StyledCol className="col-trend">
                <VFlex width="100%">
                  <Flex a="center">
                    <Space w={16} />
                    <Flex j="space-between" a="center" f="1">
                      <Typo type="label2" className="text-gray-700" weight="r">
                        Sentiment trend
                      </Typo>

                      <Checkbox
                        checked={hideNeutral}
                        onChange={(e) => {
                          setHideNeutral(e.target.checked);
                        }}
                      >
                        Hide neutral
                      </Checkbox>
                    </Flex>
                  </Flex>

                  <Flex
                    style={{ transform: 'translateY(40px)', width: '100%' }}
                  >
                    <ChartLabels
                      startDate={startDate}
                      endDate={endDate}
                      dateRange={dateRange}
                      offset={SENTIMENT_CHART_OFFSET}
                    />
                  </Flex>
                </VFlex>
              </StyledCol>

              <StyledCol className="col-sentence">
                <Space w={16} />
                <Typo type="label2" className="text-gray-700" weight="r">
                  Message
                </Typo>
              </StyledCol>

              <Space w={16} />
            </StyledTableHeader>
            <List
              className="pb-16 overflow-unset h-full"
              data={listState.list || []}
              placeholder={{
                node: (
                  <CenterFlex height="400px">
                    <Typo type="body1" style={{ textAlign: 'center' }}>
                      Looks like there aren't any customers matching that time
                      period and criteria.
                      <br />
                      No worries though, just tweak the filters and give it
                      another go!
                    </Typo>
                  </CenterFlex>
                ),
              }}
              render={(item) => {
                return (
                  <CustomerRow
                    hideNeutral={hideNeutral}
                    domain={state.domain}
                    sentiment={state.sentiment}
                    key={item.alias}
                    dateRange={dateRange}
                    startDate={startDate}
                    endDate={endDate}
                    sentimentTrendChartMode={'dot'}
                    {...item}
                  />
                );
              }}
            />
          </StyledCustomerSentimentTableWrapper>

          <StyledPaginationWrapper>
            <StyledPagination
              loading={listState.loading}
              pageSize={LENGTH_PER_PAGE}
              currentLength={listState.list?.length || 0}
              total={currCount}
              current={listState.page}
              onChange={(page) => {
                fetchList({
                  listState,
                  targetPage: page,
                });
              }}
            />
          </StyledPaginationWrapper>
        </StyledCustomerSentimentList>
      </StyledWrapper>
    </Style>
  );
};

const Style = styled(VFlex)`
  height: 100%;
`;

const StyledWrapper = styled(Flex).attrs({
  g: 60,
  f: 1,
})`
  overflow: auto;
  padding: 12px 32px 0px;
`;

const StyledCustomerSentimentList = styled(VFlex).attrs({
  f: 1,
})`
  overflow: auto;
`;

const StyledCustomerSentimentListHeader = styled(Flex).attrs({
  j: 'space-between',
})`
  padding-right: 16px;
`;

const StyledCustomerSentimentTableWrapper = styled(VFlex)`
  overflow: auto;
  width: 100%;
  flex: 1;
`;

const StyledTableHeader = styled(Flex).attrs({
  j: 'space-between',
})`
  padding: 18px 0;
  border-bottom: 1px solid ${(props) => props.theme.colors.gray300};
  border-top: 1px solid ${(props) => props.theme.colors.gray300};
  min-width: fit-content;
`;

const StyledPaginationWrapper = styled(Flex)`
  position: absolute;
  bottom: 8px;
  width: fit-content;
  padding: 8px 32px 8px 16px;
  background-color: ${(props) => props.theme.colors.gray200};
  border-radius: 12px;
`;

export default CustomerSentimentPage;
